import { gql, useMutation, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import useNaverADsScript, { NaverADsType } from "@repo/hooks/lib/useNaverADsScript";
import { fetchSocialLogin } from "@repo/shopify/src/api/fetchSocialLogin";
import { customerAccessTokenCreateMutation, customerRecoverMutation } from "@repo/shopify/src/queries/customer-gql";
import type {
  ICustomerAccessTokenCreateMutation,
  ICustomerQuery,
  ICustomerRecoverMutation,
} from "@repo/shopify/src/queries/customer-gql";
import { emailRegex } from "@repo/utils/src/regex";
import classNames from "classnames";
import _ from "lodash";
import React, { useState } from "react";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import Input from "@components/atoms/Input/Input";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { NOTIFICATIONS } from "@utils/notifications";

/** EmailLoginForm step */
const STEP = {
  LOGIN: "로그인",
  REGISTER: "회원 가입",
  RESET_PASSWORD: "비밀번호 재설정",
};

/** 소셜로그인 */
const SOCIAL_LOGIN_TYPE = {
  google: "구글",
  kakao: "카카오",
  naver: "네이버",
};

interface IProps {
  onComplete: (accessToken?: string, customer?: ICustomerQuery["customer"]) => void;
}

interface IFormData {
  email: string;
  password: string;
}

/**
 * 이메일 로그인 입력 Form
 */
function EmailLogin({ onComplete }: IProps) {
  const { userLogin, loginLoading, loginError, fetchUser, createCustomer, createCustomerLoading } = useUser();
  const { t } = useTranslation();
  const { openToast } = useLayout();

  const { tryAppendNaverADsScript } = useNaverADsScript();

  /** 이메일 로그인 단계 */
  const [step, setStep] = useState(STEP.LOGIN);
  const [isRegistration, setIsRegistration] = useState(false);
  const [firstErrorKey, setFirstErrorKey] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);

  /**
   * 입력 form hook
   * email, password Input 입력값 handler
   * */
  const {
    register,
    handleSubmit,
    getValues,
    setFocus,
    reset: resetForm,
    formState: { errors },
  } = useForm<IFormData>({ defaultValues: { email: "", password: "" } });

  /** 로그인 > customerAccessToken 생성 => 왜 useUser 훅의 createToken 을 사용하지 않는지? */
  const [createCustomerAccessToken, { loading: tokenLoading }] = useMutation<ICustomerAccessTokenCreateMutation>(
    gql(customerAccessTokenCreateMutation.query),
    {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onCompleted: async (data) => {
        setButtonLoading(true);
        const accessToken = data.customerAccessTokenCreate.customerAccessToken?.accessToken;

        if (data.customerAccessTokenCreate.customerUserErrors.length) {
          if (!accessToken) {
            // 에러 메시지 state 저장 후 종료
            const { email } = getValues();
            const loginType = await fetchSocialLogin(email ?? "", `tune`);
            const isSocialLogin = loginType !== "email" && loginType !== "";
            const socialAccount =
              SOCIAL_LOGIN_TYPE[loginType?.toLowerCase() as keyof typeof SOCIAL_LOGIN_TYPE] ?? t("login.social");

            if (isSocialLogin) {
              openToast(NOTIFICATIONS.WARN_SOCIAL_ACCOUNT({ social: socialAccount }));
              return;
            }

            openToast(NOTIFICATIONS.WARN_INVALID_CREDENTIALS);
            setButtonLoading(false);
            return;
          }

          if (data.customerAccessTokenCreate.customerUserErrors.length) {
            const error = data.customerAccessTokenCreate.customerUserErrors[0];
            openToast(
              error.code?.includes("INVALID") ? NOTIFICATIONS.WARN_INVALID_CREDENTIALS : NOTIFICATIONS.ERROR_DEFAULT,
            );
            setButtonLoading(false);
            return;
          }
        }

        // 로그인 처리
        if (loginLoading || loginError || !accessToken) return;
        /**
         * NOTE: fetchUser는 accessToken을 이용하여 customer 를 조회.
         * fetchUser의 onCompleted 콜백에서 customer 정보를 redux user에 저장하는데,
         * fetchUser가 완료되기 전에 컴포넌트가 unmount 되면서 onCompleted 콜백이 호출되지 않음.
         * => App.jsx accessToken 있는지 확인해서 페이지를 렌더링. 여기서 fetchUser 완료 전에 로그인 페이지 unmount
         *
         * accessToken 을 redux user에 저장(userLogin)하는 순서를 fetchUser 완료한 후에 하도록 수정
         */
        const { data: customerData } = await fetchUser({ variables: { customerAccessToken: accessToken } });
        userLogin(accessToken || "");

        if (isRegistration) {
          tryAppendNaverADsScript({ type: NaverADsType.Join, value: "0" });
          openToast(NOTIFICATIONS.SUCCESS_REGISTER);
          setIsRegistration(false);
        } else {
          // openToast(NOTIFICATIONS.SUCCESS_LOGIN);
        }
        onComplete(accessToken, customerData?.customer);
        setButtonLoading(false);
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CustomerAccessTokenCreate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CustomerAccessTokenCreate] Failed to mutation : ${JSON.stringify(error)}`);
        setButtonLoading(false);
      },
    },
  );

  /** 비밀번호 재설정 이메일 전송 쿼리 */
  const [resetPasswordEmailHandle] = useMutation<ICustomerRecoverMutation>(gql(customerRecoverMutation.query), {
    onCompleted: () => {
      const { email } = getValues();
      openToast(NOTIFICATIONS.SUCCESS_PASSWORD_RESET({ email }));
      setStep(STEP.LOGIN);
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CustomerRecover] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CustomerRecover] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  /**
   * Submit(모달 하단 오른쪽) 버튼 클릭 핸들러
   */
  const onSubmit = async (data: IFormData) => {
    const { email } = getValues();
    const loginType = await fetchSocialLogin(email ?? "", `tune`);
    const isSocialLogin = loginType !== "email" && loginType !== "";
    const socialAccount =
      SOCIAL_LOGIN_TYPE[loginType?.toLowerCase() as keyof typeof SOCIAL_LOGIN_TYPE] ?? t("login.social");

    // 소셜 로그인인 경우
    if (isSocialLogin) {
      switch (step) {
        case STEP.LOGIN:
          openToast(NOTIFICATIONS.WARN_SOCIAL_ACCOUNT({ social: socialAccount }));
          return;
        case STEP.REGISTER:
          openToast(NOTIFICATIONS.WARN_SOCIAL_ACCOUNT_TAKEN({ social: socialAccount }));
          return;
        case STEP.RESET_PASSWORD:
          openToast(NOTIFICATIONS.WARN_RESET_PASSWORD);
          return;
      }
    }

    // 이메일 로그인인 경우
    switch (step) {
      case STEP.LOGIN:
        // 로그인일 때 customerAccessToken 생성
        await createCustomerAccessToken({ variables: { input: data } });
        break;

      case STEP.REGISTER:
        await createCustomer({ variables: { input: data } }).then(({ data: createData }) => {
          if (createData?.customerCreate.userErrors.length) return;
          const value = getValues();
          setIsRegistration(true);
          createCustomerAccessToken({ variables: { input: value } });
        });
        break;

      case STEP.RESET_PASSWORD:
        await resetPasswordEmailHandle({ variables: { email: data.email } });
        break;
    }
  };

  const debouncedSubmit = _.debounce(async (data) => {
    if (step === STEP.LOGIN) {
      await createCustomerAccessToken({ variables: { input: data } });
    } else if (step === STEP.REGISTER) {
      await createCustomer({ variables: { input: data } }).then(({ data: createData }) => {
        if (createData?.customerCreate.userErrors.length) return;

        const value = getValues();
        setIsRegistration(true);
        createCustomerAccessToken({ variables: { input: value } });
      });
    }
  }, 1000);

  const onError: SubmitErrorHandler<IFormData> = (error) => {
    const sortedKeys = Object.keys(getValues());
    sortedKeys.some((key) => {
      if (error[key as keyof typeof error]) {
        setFirstErrorKey(key);
        return true;
      }
      return false;
    });
  };

  const displayError = (key: string) => {
    return { hasError: firstErrorKey === key };
  };

  return (
    <div className="bg-tuneGrey-100 rounded-[10px] p-[20px]">
      <h2 className="mb-[20px] text-left text-black">{step}</h2>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="mb-[20px] flex flex-col gap-[10px]">
          <Input
            {...displayError("email")}
            {...register("email", {
              required: NOTIFICATIONS.WARN_INVALID_CREDENTIALS.message,
              pattern: {
                value: emailRegex,
                message: NOTIFICATIONS.WARN_EMAIL_ACCOUNT.message,
              },
            })}
            className="text-black"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                setFocus("password");
              }
            }}
            placeholder="이메일 주소"
            variant="white"
          />
          {step !== STEP.RESET_PASSWORD && (
            <Input
              {...displayError("password")}
              {...register("password", {
                validate(value) {
                  if (!value.trim()) return NOTIFICATIONS.WARN_INVALID_CREDENTIALS.message;
                },
              })}
              className="text-black"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.stopPropagation();

                  const data = getValues();
                  debouncedSubmit(data);
                }
              }}
              placeholder={t("login.password")}
              type="password"
              variant="white"
            />
          )}
          {step === STEP.LOGIN && (
            <div>
              <Button
                className="text-tuneGrey-400 inline bg-transparent text-left text-sm underline"
                onClick={() => {
                  setStep(STEP.RESET_PASSWORD);
                }}
                style={{ padding: 0 }}
              >
                {t("login.reset_password")}
              </Button>
            </div>
          )}
        </div>
      </form>
      <div className="flex justify-center gap-[10px]">
        <Button
          onClick={() => {
            setStep(step === STEP.LOGIN ? STEP.REGISTER : STEP.LOGIN);
            resetForm();
          }}
          styleType="secondary"
          variant="contained"
        >
          {step === STEP.LOGIN ? t("login.register") : t("login.cancel")}
        </Button>
        {/* Submit 버튼 */}
        <Button
          className={classNames(
            step === STEP.LOGIN && "w-[86.64px]",
            step === STEP.REGISTER && "w-[102.86px]",
            step === STEP.RESET_PASSWORD && "w-[141.49px]",
          )}
          isLoading={tokenLoading || createCustomerLoading || buttonLoading}
          onClick={handleSubmit(onSubmit, onError)}
          styleType="primary"
          type="submit"
          variant="contained"
        >
          {step === STEP.LOGIN
            ? t("login.login")
            : step === STEP.REGISTER
              ? t("login.register")
              : t("login.send_email")}
        </Button>
      </div>
    </div>
  );
}

export default EmailLogin;
